import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import buttonCss from './lib-button.css';

export type LibButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'delete';

/**
 * The WAGO button as a custom element - styling follows the "button" component set of the
 * UI Core Library Figma design; see lib-button.css for the geometry, colour and state rules.
 *
 * Slotted content becomes the label. Clicks are native composed events and bubble out of
 * the shadow root, so a plain `click` listener (or the Blazor `onclick`) just works.
 */
@customElement('lib-button')
export class LibButton extends LibElement {
    protected override get hostFocusSelector(): string { return 'a, button'; }
    static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(buttonCss)];

    static readonly formAssociated = true;
    readonly #internals?: ElementInternals;

    constructor() {
        super();
        try {
            this.#internals = this.attachInternals();
        } catch {
            this.#internals = undefined;
        }
    }

    /** Visual hierarchy: colours and emphasis of the Figma "button" component set. */
    @property({ reflect: true })
    variant: LibButtonVariant = 'primary';

    @property() href = '';
    @property() target = '';
    @property() rel = '';

    /** Renders the disabled state and suppresses clicks. */
    @property({ type: Boolean, reflect: true })
    disabled = false;

    @property({ type: Boolean, reflect: true })
    loading = false;

    /** Stretches the button to the full width of its container. */
    @property({ type: Boolean, reflect: true, attribute: 'full-width' })
    fullWidth = false;

    /** Regular font weight instead of bold (design option of the tertiary hierarchy). */
    @property({ type: Boolean, reflect: true })
    regular = false;

    /** Submits the surrounding form on click (shadow-root buttons cannot do that natively). */
    @property({ type: Boolean })
    submit = false;

    /** Native tooltip of the button (forwarded HTML title attribute). */
    @property()
    title = '';

    @property({ attribute: 'aria-label' })
    override ariaLabel: string | null = null;

    @property({ attribute: 'aria-expanded' })
    override ariaExpanded: string | null = null;

    @property({ attribute: 'aria-controls' })
    controls: string | null = null;

    override render() {
        const classes = [
            'lib-button',
            `lib-button-${this.variant}`,
            this.regular ? 'lib-button-regular' : '',
        ];

        if (this.href) {
            const blocked = this.disabled || this.loading;
            return html`
                <a
                    part="button"
                    class=${classes.filter(Boolean).join(' ')}
                    href=${blocked ? nothing : this.href}
                    target=${this.target || nothing}
                    rel=${this.rel || nothing}
                    role=${blocked ? 'link' : nothing}
                    aria-disabled=${blocked ? 'true' : nothing}
                    tabindex=${blocked ? '-1' : nothing}
                    title=${this.title || nothing}
                    aria-label=${this.ariaLabel || nothing}
                    aria-expanded=${this.ariaExpanded ?? nothing}
                    @click=${this.#onClick}
                ><slot></slot></a>
            `;
        }

        return html`
            <button
                part="button"
                class=${classes.filter(Boolean).join(' ')}
                type=${this.submit ? 'submit' : 'button'}
                ?disabled=${this.disabled}
                title=${this.title || nothing}
                aria-label=${this.ariaLabel || nothing}
                aria-expanded=${this.ariaExpanded ?? nothing}
                @click=${this.#onClick}
            >
                <slot></slot>
            </button>
        `;
    }

    protected override updated(): void {
        const button = this.renderRoot.querySelector('a, button');
        const panel = this.controls ? (this.getRootNode() as Document | ShadowRoot).querySelector(`[id="${CSS.escape(this.controls)}"]`) : null;
        if (button) button.ariaControlsElements = panel ? [panel] : [];
    }

    #onClick(event: MouseEvent): void {
        if (this.disabled || this.loading) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
        }

        if (this.submit && !this.href) {
            this.#internals?.form?.requestSubmit();
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'lib-button': LibButton;
    }
}
